import { getApiEndpoint } from '../config/api.js';

export interface StockQuote {
  symbol: string;
  bidPrice: number;
  bidSize: number;
  askPrice: number;
  askSize: number;
  lastPrice: number;
  lastSize: number;
  lastUpdated: number;
  volume: number;
}

export interface CompanyInfo {
  symbol: string;
  name: string;
  description: string;
  sector: string;
  industry: string;
  marketCap: number;
  employees: number;
  website: string;
  brandingIconUrl?: string;
  brandingLogoUrl?: string;
  cik?: string;
  securityFigi?: string;
  compositeFigi?: string;
  shareClassFigi?: string;
}

export interface HistoricalData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  vwap: number;
  transactions: number;
}

export interface StockSearchResult {
  symbol: string;
  name: string;
  market: string;
  locale: string;
  type: string;
  active: boolean;
  currency_name?: string;
  cik?: string;
  figi?: string;
  securityFigi?: string;
  compositeFigi?: string;
  shareClassFigi?: string;
  composite_figi?: string;
  share_class_figi?: string;
  last_updated_utc?: string;
}

export interface StockSearchResponse {
  data: StockSearchResult[];
  count: number;
  next_url?: string;
  request_id?: string;
  status: string;
}

export const polygonApi = {
  async getStockQuote(symbol: string): Promise<StockQuote> {
    const response = await fetch(`${getApiEndpoint()}/stockData?symbol=${encodeURIComponent(symbol)}&action=quote`);
    
    if (!response.ok) {
      throw new Error(`Stock quote API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: StockQuote };
    return data.data;
  },

  async getBatchStockQuotes(symbols: string[]): Promise<StockQuote[]> {
    const symbolsParam = symbols.map(s => encodeURIComponent(s)).join(',');
    const response = await fetch(`${getApiEndpoint()}/stockData?symbols=${symbolsParam}&action=quote`);
    
    if (!response.ok) {
      throw new Error(`Batch stock quotes API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: StockQuote[] };
    return data.data;
  },

  async getCompanyInfo(symbol: string): Promise<CompanyInfo> {
    const response = await fetch(`${getApiEndpoint()}/stockData?symbol=${encodeURIComponent(symbol)}&action=company`);
    
    if (!response.ok) {
      throw new Error(`Company info API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: CompanyInfo };
    return data.data;
  },

  async getBatchCompanyInfo(symbols: string[]): Promise<CompanyInfo[]> {
    const symbolsParam = symbols.map(s => encodeURIComponent(s)).join(',');
    const response = await fetch(`${getApiEndpoint()}/stockData?symbols=${symbolsParam}&action=company`);
    
    if (!response.ok) {
      throw new Error(`Batch company info API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: CompanyInfo[] };
    return data.data;
  },

  async getStockPrice(symbol: string): Promise<number> {
    const response = await fetch(`${getApiEndpoint()}/stockData?symbol=${encodeURIComponent(symbol)}&action=price`);
    
    if (!response.ok) {
      throw new Error(`Stock price API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: { symbol: string; price: number } };
    return data.data.price;
  },

  async getBatchStockPrices(symbols: string[]): Promise<Array<{ symbol: string; price: number }>> {
    const symbolsParam = symbols.map(s => encodeURIComponent(s)).join(',');
    const response = await fetch(`${getApiEndpoint()}/stockData?symbols=${symbolsParam}&action=price`);
    
    if (!response.ok) {
      throw new Error(`Batch stock prices API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: Array<{ symbol: string; price: number }> };
    return data.data;
  },

  async getMarketCap(symbol: string): Promise<number> {
    const response = await fetch(`${getApiEndpoint()}/stockData?symbol=${encodeURIComponent(symbol)}&action=marketcap`);
    
    if (!response.ok) {
      throw new Error(`Market cap API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: { symbol: string; marketCap: number } };
    return data.data.marketCap;
  },

  async getBatchMarketCap(symbols: string[]): Promise<Array<{ symbol: string; marketCap: number }>> {
    const symbolsParam = symbols.map(s => encodeURIComponent(s)).join(',');
    const response = await fetch(`${getApiEndpoint()}/stockData?symbols=${symbolsParam}&action=marketcap`);
    
    if (!response.ok) {
      throw new Error(`Batch market cap API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: Array<{ symbol: string; marketCap: number }> };
    return data.data;
  },

  async getHistoricalData(symbol: string, days: number = 30): Promise<HistoricalData[]> {
    const response = await fetch(`${getApiEndpoint()}/stockData?symbol=${encodeURIComponent(symbol)}&action=historical&days=${days}`);
    
    if (!response.ok) {
      throw new Error(`Historical data API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: HistoricalData[]; symbol: string; days: number };
    return data.data;
  },

  async getBatchHistoricalData(symbols: string[], days: number = 30): Promise<Array<{ symbol: string; data: HistoricalData[]; days: number }>> {
    const symbolsParam = symbols.map(s => encodeURIComponent(s)).join(',');
    const response = await fetch(`${getApiEndpoint()}/stockData?symbols=${symbolsParam}&action=historical&days=${days}`);
    
    if (!response.ok) {
      throw new Error(`Batch historical data API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: Array<{ symbol: string; data: HistoricalData[]; days: number }> };
    return data.data;
  },

  async getBatchCombinedData(symbols: string[], days: number = 2): Promise<Array<{ symbol: string; quote: StockQuote; companyInfo: CompanyInfo; historicalData: HistoricalData[] }>> {
    const symbolsParam = symbols.map(s => encodeURIComponent(s)).join(',');
    const response = await fetch(`${getApiEndpoint()}/stockData?symbols=${symbolsParam}&action=combined&days=${days}`);
    
    if (!response.ok) {
      throw new Error(`Batch combined data API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: Array<{ symbol: string; quote: StockQuote; companyInfo: CompanyInfo; historicalData: HistoricalData[] }> };
    return data.data;
  },

  async searchStocks(query: string, limit: number = 10): Promise<StockSearchResult[]> {
    const response = await fetch(`${getApiEndpoint()}/stockData?action=search&query=${encodeURIComponent(query)}&limit=${limit}`);
    
    if (!response.ok) {
      throw new Error(`Stock search API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as StockSearchResponse;
    return data.data;
  }
}; 
